import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { validatePassword } from '../../utils/validation';
import AuthLayout from '../../components/Layout/Auth/AuthLayout';
import CreatePasswordCard from './CreatePasswordCard';
import CreatePasswordContent from './CreatePasswordContent';
import { CONSTS } from '../../const';
import useAuthTokens from '../../utils/hooks/useAuthTokens';
import { getErrMsgFromResp, handleErrorToast } from '../../utils/errorHandler';
import { rsaencrypt } from '../../utils/encryptpassword';
import { graphqlClient } from '../../utils/client/graphql';
import { INIT_MUTATION, SET_PASSWORD_MUTATION } from '../../graphql/queries';

const CreatePassword = (props) => {
  const [loading, setLoading] = useState(false);
  const { isForgotFlow } = props;
  const { setTokens } = useAuthTokens();
  const location = useLocation();
  const navigate = useNavigate();
  const { email } = location.state || {};
  const { verificationkey } = location.state;
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    // Prime the session: the graphql client captures the publicKey internally,
    // so `rsaencrypt` will always use the latest one.
    graphqlClient.request(INIT_MUTATION).catch((err) => {
      console.log(err);
    });
  }, []);

  useEffect(() => {
    // Redirect if no email in state (prevents direct access)
    if (!email) {
      navigate(isForgotFlow ? '/forgot-password' : '/signup');
    }
  }, [email, navigate, isForgotFlow]);

  const handleSetPassword = async () => {
    setError('');
    setLoading(true);

    const passwordValidation = validatePassword(password, confirmPassword);
    if (!passwordValidation.isValid) {
      setError(passwordValidation.error);
      return;
    }

    try {
      // Variables are passed as a thunk so a 428-triggered re-init can
      // re-evaluate them — `rsaencrypt` will pick up the freshly-issued
      // publicKey from the graphql client on the retry.
      const response = await graphqlClient.request(SET_PASSWORD_MUTATION, () => ({
        input: {
          email: email,
          password: rsaencrypt(password),
          confirmPassword: rsaencrypt(confirmPassword),
          key: verificationkey,
        },
      }));

      const setPwd = response?.setPassword;
      const status = setPwd?.status;
      const message = setPwd?.message;
      const data = setPwd?.data;

      if (status === CONSTS.OK) {
        // Store tokens
        if (data?.accessToken) {
          setTokens({
            accessToken: data.accessToken,
          });
        }
        if (isForgotFlow) {
          navigate('/login', {
            state: {
              message:
                'Password reset successfully. Please login with your new password.',
            },
          });
        } else if (data?.redirectionData?.viewIdentifier && data?.accessToken) {
          const dataToPass = {
            accessToken: data.accessToken,
          };

          const url = data?.redirectionData?.viewIdentifier;
          const allowedDomainsEnv = process.env.REACT_APP_ALLOWED_DOMAINS;
          const allowedDomains = allowedDomainsEnv
            ? allowedDomainsEnv.split(',')
            : [];

          const isAllowedDomain = allowedDomains.some(
            (domain) => domain == url
          );

          if (isAllowedDomain) {
            const encodedData = btoa(JSON.stringify(dataToPass));
            console.log('in login success');
            const redirectUrl = `${url}?data=${encodedData}`;
            window.location.href = redirectUrl;
          } else {
            console.error('Redirect URL not allowed:', url);
          }
        }
      } else {
        throw response;
      }
    } catch (err) {
      setError(getErrMsgFromResp(err));
      console.error('Password error:', err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    handleErrorToast(error);
    setTimeout(() => {
      setError('');
    }, 500);
  }, [error]);

  return (
    <AuthLayout leftContent={<CreatePasswordContent {...props} />}>
      <CreatePasswordCard
        email={email}
        password={password}
        setPassword={setPassword}
        confirmPassword={confirmPassword}
        setConfirmPassword={setConfirmPassword}
        error={error}
        handleSetPassword={handleSetPassword}
        isForgotFlow={isForgotFlow}
        loading={loading}
      />
    </AuthLayout>
  );
};

export default CreatePassword;
